// What one touch of the Diagonal brush does to one square (docs/architecture/
// maps.md, "Library and terrain"): a cut square is made whole; a rock or a whole
// floor square is cut along the corner its neighbours settle and, for a tap
// only, failing that along the one nearest the touch. The half cut off takes
// what lies across the two sides its corner touches: rock, or the floor beside it.
import { borderAt, type CutLetter, codeAlong, cutAt, floorAlong, layCell, ROCK_SIDES, rougher, type Side, type Terrain, terrainOf } from "./cuts";
import { EMPTY } from "./terrain";

type Cell = { col: number; row: number };
// Where in the cell the touch fell, as fractions 0..1 from its north-west corner.
type Pointer = { x: number; y: number };
// What the tray's chip arms the board's brush with, where a tile arms it with its code.
export const DIAGONAL = "diagonal";
// `corner`: the floor of the half cut off, absent when that half is rock. `border`: the neighbour's, set when the change
// lays floor on a rock cell, and the rougher one when the cut-off half takes a floor; otherwise the cell keeps its own.
export type CellChange = { code: string; cut: CutLetter | null; corner?: string; border?: string };

const SIDES: readonly Side[] = ["n", "e", "s", "w"];
// A neighbour's step, and the side of it that faces this cell.
const NEIGHBOUR: Record<Side, readonly [number, number, Side]> = { n: [0, -1, "s"], e: [1, 0, "w"], s: [0, 1, "n"], w: [-1, 0, "e"] };
const OPPOSITE: Record<CutLetter, CutLetter> = { a: "c", b: "d", c: "a", d: "b" };

// The terrain as `rows` and `cuts`. `open` says whether a cell can be painted,
// which the scene's edge and storage decide: one that cannot is never changed
// and is rock to its neighbours. Only a tap may `fallback` to the corner nearest
// the touch where the neighbours do not settle the cell; a swipe leaves such a
// cell alone, so a finger grazing a neighbour never cuts it.
export function diagonalChange(
	terrain: { rows: readonly string[]; cuts?: readonly string[]; corners?: readonly string[]; borders?: readonly string[] },
	cell: Cell,
	pointer: Pointer,
	{ open = () => true, fallback = false }: { open?: (col: number, row: number) => boolean; fallback?: boolean } = {},
): CellChange | null {
	const { rows, cuts, corners, borders } = terrain;
	const { col, row } = cell;
	const code = rows[row]?.[col];
	if (!open(col, row)) return null;
	if (code && code !== EMPTY) {
		if (cutAt(cuts, col, row)) return { code, cut: null };
		const settled = cornerBetween(floorSides(rows, cuts, corners, cell, open, false));
		if (settled) return { code, cut: settled };
		const across = (side: Side) => acrossCode(terrain, cell, side, open);
		const other = SIDES.filter((side) => across(side) && across(side) !== code);
		const between = cornerBetween(other);
		if (between && across(other[0]) === across(other[1])) return halfOf(terrain, cell, code, between, across(other[0]) as string, open);
		if (!fallback) return null;
		const letter = nearest(pointer);
		const legs = ROCK_SIDES[letter];
		if (legs.some((side) => !across(side))) return { code, cut: letter };
		const foreign = legs.filter((side) => across(side) !== code);
		const leg = foreign.find((side) => side === "n" || side === "s") ?? foreign[0];
		return leg ? halfOf(terrain, cell, code, letter, across(leg) as string, open) : { code, cut: letter };
	}
	const floor = floorSides(rows, cuts, corners, cell, open, true);
	if (!floor.length) return null;
	const adjacent = cornerBetween(floor);
	if (!adjacent && !fallback) return null;
	const letter = adjacent ? OPPOSITE[adjacent] : nearest(pointer);
	// Its floor half must reach a floor neighbour by one of its two legs.
	if (!adjacent && SIDES.every((side) => ROCK_SIDES[letter].includes(side) || !floor.includes(side))) return null;
	const from = adjacent ? floor.find((side) => side === "n" || side === "s") : SIDES.find((side) => floor.includes(side));
	const [dc, dr] = NEIGHBOUR[from as Side];
	return { code: neighbourCode(rows, cell, from as Side), cut: letter, border: borderAt(borders, col + dc, row + dr) };
}

// A new terrain with these cells' codes, cuts and laid borders, normalised once.
export function applyCells(terrain: Terrain, changes: { cell: Cell; change: CellChange }[]): Terrain {
	if (!changes.length) return terrain;
	const [rows, cuts, corners, borders] = [[...terrain.rows], [...terrain.cuts], [...(terrain.corners ?? [])], [...(terrain.borders ?? [])]];
	for (const { cell, change } of changes) {
		layCell(rows, cell.col, cell.row, change.code);
		layCell(cuts, cell.col, cell.row, change.cut ?? EMPTY);
		layCell(corners, cell.col, cell.row, change.corner ?? EMPTY);
		if (change.border) layCell(borders, cell.col, cell.row, change.border);
	}
	return terrainOf(rows, cuts, corners, borders);
}

// The sides with floor toward the cell, or, for `rock`, the sides without.
function floorSides(
	rows: readonly string[],
	cuts: readonly string[] | undefined,
	corners: readonly string[] | undefined,
	{ col, row }: Cell,
	open: (col: number, row: number) => boolean,
	floor: boolean,
) {
	return SIDES.filter((side) => {
		const [dc, dr, facing] = NEIGHBOUR[side];
		return (open(col + dc, row + dr) && floorAlong(rows, cuts, col + dc, row + dr, facing, corners)) === floor;
	});
}

type TerrainLines = Parameters<typeof diagonalChange>[0];
type Open = (col: number, row: number) => boolean;

// The floor code the neighbour on `side` presents toward the cell, null for rock or a neighbour that cannot be painted.
function acrossCode({ rows, cuts, corners }: TerrainLines, { col, row }: Cell, side: Side, open: Open) {
	const [dc, dr, facing] = NEIGHBOUR[side];
	return open(col + dc, row + dr) ? codeAlong(rows, cuts, corners, col + dc, row + dr, facing) : null;
}

// A cut along `letter` whose cut-off half is the floor `corner`, with the roughest border of the cell and the neighbours across its two legs that present it.
function halfOf(terrain: TerrainLines, cell: Cell, code: string, letter: CutLetter, corner: string, open: Open): CellChange {
	let border = borderAt(terrain.borders, cell.col, cell.row);
	for (const side of ROCK_SIDES[letter]) {
		if (acrossCode(terrain, cell, side, open) !== corner) continue;
		const [dc, dr] = NEIGHBOUR[side];
		border = rougher(border, borderAt(terrain.borders, cell.col + dc, cell.row + dr));
	}
	return { code, cut: letter, corner, border };
}

// The corner two adjacent sides meet at, when they are exactly those two.
function cornerBetween(sides: Side[]): CutLetter | null {
	if (sides.length !== 2) return null;
	return (Object.keys(ROCK_SIDES) as CutLetter[]).find((letter) => ROCK_SIDES[letter].every((side) => sides.includes(side))) ?? null;
}

function nearest({ x, y }: Pointer): CutLetter {
	if (y < 0.5) return x < 0.5 ? "d" : "a";
	return x < 0.5 ? "c" : "b";
}

function neighbourCode(rows: readonly string[], { col, row }: Cell, side: Side) {
	const [dc, dr] = NEIGHBOUR[side];
	return rows[row + dr]?.[col + dc] as string;
}
